import { Component, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { describeFilingUpload, buildFilingUpload } from '../../engine/filing-upload';
import { FileUploadZoneComponent } from '../../shared/components/file-upload-zone/file-upload-zone.component';
import { JobStatusLineComponent } from '../../shared/components/job-status-line/job-status-line.component';
import { PyodideArelleEngine } from '../../engine/pyodide-arelle.engine';
import type { ReportsDebugRow } from '../../engine/xbrl-engine.types';

@Component({
  selector: 'app-report-viewer',
  imports: [FileUploadZoneComponent, JobStatusLineComponent, RouterLink],
  templateUrl: './report-viewer.component.html',
  styleUrl: './report-viewer.component.scss',
})
export class ReportViewerComponent {
  private readonly engine = inject(PyodideArelleEngine);

  protected readonly phase = signal<string | null>(null);
  protected readonly busy = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly filename = signal<string | null>(null);
  protected readonly debugRows = signal<ReportsDebugRow[]>([]);
  protected readonly downloadName = signal<string | null>(null);
  private xlsxBytes: Uint8Array | null = null;

  protected onFiles(files: File[]): void {
    if (!files.length || this.busy()) return;
    void this.runExport(files);
  }

  private async runExport(files: File[]): Promise<void> {
    this.busy.set(true);
    this.error.set(null);
    this.phase.set('starting');
    this.debugRows.set([]);
    this.downloadName.set(null);
    this.xlsxBytes = null;

    try {
      const payload = await buildFilingUpload(files);
      this.filename.set(describeFilingUpload(payload));

      this.engine.exportReports(files).subscribe({
        next: (event) => {
          if (event.kind === 'progress') {
            this.phase.set(event.data.phase);
          } else {
            this.debugRows.set(event.data.debugRows);
            this.downloadName.set(event.data.downloadFilename);
            this.xlsxBytes = event.data.xlsxBytes;
            this.phase.set('ready');
            this.busy.set(false);
          }
        },
        error: (err: unknown) => {
          this.error.set(err instanceof Error ? err.message : String(err));
          this.busy.set(false);
          this.phase.set(null);
        },
      });
    } catch (err: unknown) {
      this.error.set(err instanceof Error ? err.message : String(err));
      this.busy.set(false);
      this.phase.set(null);
    }
  }

  protected download(): void {
    const name = this.downloadName();
    const bytes = this.xlsxBytes;
    if (!name || !bytes) return;
    const copy = bytes.slice();
    const blob = new Blob([copy], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = name;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  protected formatValue(value: string | number | null): string {
    if (value === null || value === undefined) return '';
    return String(value);
  }
}
